<template>
  <div class="gacha-machine">
    <!-- 유리 돔(통) 시각 + 물리 캔버스 -->
    <div class="tank-glow"></div>
    <div ref="canvasHost" class="canvas-host"></div>
    <!-- 출구 트레이 입구 -->
    <div class="chute"></div>
  </div>
</template>

<script>
import Matter from "matter-js";
import { ALL_MODEL_INDEXES } from "../constants";

const W = 380;
const H = 448;
const FLOOR_Y = 318; // 통 바닥 y
const BALL_R = 18; // 공 반지름

// 충돌 카테고리
const CAT_WALL = 0x0001; // 벽 + 트레이
const CAT_FLOOR = 0x0002; // 통 바닥(당첨 공만 통과)
const CAT_BALL = 0x0004;
const MASK_ALL = CAT_WALL | CAT_FLOOR | CAT_BALL;
const MASK_NO_FLOOR = CAT_WALL | CAT_BALL; // 바닥만 무시 → 통과

function thumbUrl(idx) {
  return `/trial/thumbnails/${idx}_thumbnail.png`;
}

export default {
  name: "GachaMachine",
  props: {
    // 통에 넣을 동물 목록. 기본 = 전체 카탈로그(28종).
    pool: {
      type: Array,
      default: () => ALL_MODEL_INDEXES,
    },
  },
  data() {
    return {
      engine: null,
      render: null,
      runner: null,
      balls: [], // 통 안 공 바디 (label = modelIndex)
      winnerBody: null,
      texInfo: {}, // idx -> { texture, w, h }
    };
  },
  async mounted() {
    await this.preload();
    this.initPhysics();
  },
  beforeDestroy() {
    this.teardown();
  },
  methods: {
    // 썸네일 자연 크기 선로딩(스프라이트 스케일 계산용)
    preload() {
      const idxs = Array.from(new Set(this.pool));
      return Promise.all(
        idxs.map(
          (idx) =>
            new Promise((resolve) => {
              const img = new Image();
              img.onload = () => {
                this.texInfo[idx] = { texture: thumbUrl(idx), w: img.naturalWidth || 200, h: img.naturalHeight || 200 };
                resolve();
              };
              img.onerror = () => {
                this.texInfo[idx] = { texture: thumbUrl(idx), w: 200, h: 200 };
                resolve();
              };
              img.src = thumbUrl(idx);
            }),
        ),
      );
    },
    spriteRender(idx, r) {
      const info = this.texInfo[idx] || { texture: thumbUrl(idx), w: 200, h: 200 };
      return { sprite: { texture: info.texture, xScale: (2 * r) / info.w, yScale: (2 * r) / info.h } };
    },
    initPhysics() {
      const { Engine, Render, Runner, Composite, Bodies, Body } = Matter;
      const host = this.$refs.canvasHost;
      if (!host) return;

      const engine = Engine.create();
      engine.gravity.y = 1;
      this.engine = engine;

      const render = Render.create({
        element: host,
        engine,
        options: { width: W, height: H, wireframes: false, background: "transparent", pixelRatio: window.devicePixelRatio || 1 },
      });
      this.render = render;

      const wall = (x, y, w, h, category) =>
        Bodies.rectangle(x, y, w, h, {
          isStatic: true,
          render: { visible: false },
          collisionFilter: { category, mask: MASK_ALL },
        });

      // 통: 천장/좌/우 + 바닥(당첨 공만 통과하는 CAT_FLOOR) + 아래 출구 트레이(CAT_WALL)
      const walls = [
        wall(W / 2, 8, W, 20, CAT_WALL), // 천장
        wall(18, FLOOR_Y / 2 + 8, 16, FLOOR_Y, CAT_WALL), // 좌
        wall(W - 18, FLOOR_Y / 2 + 8, 16, FLOOR_Y, CAT_WALL), // 우
        wall(W / 2, FLOOR_Y, W, 16, CAT_FLOOR), // 통 바닥 (통과 대상)
        wall(W / 2, H - 12, 124, 16, CAT_WALL), // 트레이 바닥
        wall(W / 2 - 64, H - 40, 14, 64, CAT_WALL), // 트레이 좌
        wall(W / 2 + 64, H - 40, 14, 64, CAT_WALL), // 트레이 우
      ];
      Composite.add(engine.world, walls);

      // 통 안 공: pool 전체(28종)
      this.balls = this.pool.map((idx, i) => {
        const cols = 10;
        const x = 44 + (i % cols) * 30 + ((i % 2) * 6);
        const y = 46 + Math.floor(i / cols) * 32;
        const b = Bodies.circle(x, y, BALL_R, {
          label: idx, // 결과 식별자
          restitution: 0.5,
          friction: 0.02,
          frictionAir: 0.012,
          collisionFilter: { category: CAT_BALL, mask: MASK_ALL },
          render: this.spriteRender(idx, BALL_R),
        });
        return b;
      });
      Composite.add(engine.world, this.balls);

      const runner = Runner.create();
      Runner.run(runner, engine);
      Render.run(render);
      this.runner = runner;
      Body && this.balls.forEach((b) => Body.applyForce(b, b.position, { x: (Math.random() - 0.5) * 0.005, y: 0 }));
    },

    // 통을 흔들어 섞고, "출구에 가장 가까운 공"을 결과로 골라 트레이로 떨어뜨린다.
    // resolve(modelIndex) — 물리적으로 결정된 결과를 반환.
    spin() {
      const { Body, Events } = Matter;
      const engine = this.engine;
      return new Promise((resolve) => {
        if (!engine) return resolve(null);

        // 이전 당첨 공을 통 안으로 복귀
        if (this.winnerBody) {
          this.resetBallIntoTank(this.winnerBody);
          this.winnerBody = null;
        }

        // 1) 흔들기 — 통 전체를 흔드는 느낌으로 '중력 벡터'를 좌우/상하 진동 + 개별 지터.
        //    (바닥에 깔린 공도 다 같이 출렁이며 충돌 → 골고루 섞임)
        const SHAKE_MS = 1500;
        const STEP = 55;
        let t = 0;
        const shake = setInterval(() => {
          t += 1;
          const p = Math.min(1, (t * STEP) / SHAKE_MS); // 진행도 0→1
          // 사다리꼴 엔벨로프: 짧은 ease-in(~15%) → 최고조 유지(~15%~78%) → ease-out(~78%~)
          let env;
          if (p < 0.15) env = Math.pow(p / 0.15, 1.3); // 빠른 진입(ease-in)
          else if (p < 0.78) env = 1; // 최고조 지속(plateau)
          else env = Math.pow((1 - p) / 0.22, 0.8); // 잦아듦(ease-out)
          const freq = 0.7 * (1 + p * 0.9); // 갈수록 빨라지는 진동(긴장감 고조)
          engine.gravity.x = Math.sin(t * freq) * 2.6 * env; // 좌우 흔들기
          engine.gravity.y = Math.cos(t * freq * 0.8) * 2.2 * env; // 상하 뒤집기
          const jit = 0.028 * env;
          this.balls.forEach((b) =>
            Body.applyForce(b, b.position, { x: (Math.random() - 0.5) * jit, y: (Math.random() - 0.5) * jit }),
          );
        }, STEP);
        setTimeout(() => {
          clearInterval(shake);
          engine.gravity.x = 0;
          engine.gravity.y = 1; // 중력 복구 → 아래로 가라앉으며 안정
        }, SHAKE_MS);

        // 2) 잠시 안정시킨 뒤, 출구(W/2, FLOOR_Y)에 가장 가까운 공 선택
        setTimeout(() => {
          let winner = null;
          let best = Infinity;
          for (const b of this.balls) {
            const dx = b.position.x - W / 2;
            const dy = b.position.y - FLOOR_Y;
            const d = dx * dx + dy * dy;
            if (d < best) {
              best = d;
              winner = b;
            }
          }
          if (!winner) return resolve(null);
          this.winnerBody = winner;

          // 3) 당첨 공만 바닥 통과 → 중앙으로 흘러 트레이에 낙하
          winner.collisionFilter.mask = MASK_NO_FLOOR;
          Body.setVelocity(winner, { x: (W / 2 - winner.position.x) * 0.05, y: 3 });

          let settled = false;
          const onTick = () => {
            // 중앙으로 약하게 유도
            if (winner.position.y > FLOOR_Y && Math.abs(winner.position.x - W / 2) > 4) {
              Body.applyForce(winner, winner.position, { x: (W / 2 - winner.position.x) * 0.00012, y: 0 });
            }
            if (!settled && winner.position.y > H - 80 && Math.abs(winner.velocity.y) < 0.7) {
              settled = true;
              Events.off(engine, "afterUpdate", onTick);
              resolve(winner.label);
            }
          };
          Events.on(engine, "afterUpdate", onTick);
          // 안전 장치
          setTimeout(() => {
            if (!settled) {
              settled = true;
              Events.off(engine, "afterUpdate", onTick);
              resolve(winner.label);
            }
          }, 1800);
        }, 2050); // SHAKE_MS(1500) + 안정 ~550ms 후 출구 최근접 공 선택
      });
    },

    resetBallIntoTank(b) {
      const { Body } = Matter;
      Body.setPosition(b, { x: 40 + Math.random() * (W - 80), y: 46 + Math.random() * 40 });
      Body.setVelocity(b, { x: 0, y: 0 });
      Body.setAngularVelocity(b, 0);
      b.collisionFilter.mask = MASK_ALL;
    },

    teardown() {
      const { Render, Runner, Composite, Engine } = Matter;
      try {
        if (this.render) {
          Render.stop(this.render);
          if (this.render.canvas && this.render.canvas.parentNode) this.render.canvas.parentNode.removeChild(this.render.canvas);
          this.render.canvas = null;
          this.render.context = null;
          this.render.textures = {};
        }
        if (this.runner) Runner.stop(this.runner);
        if (this.engine) {
          Composite.clear(this.engine.world, false);
          Engine.clear(this.engine);
        }
      } catch (e) {
        // no-op
      }
      this.engine = this.render = this.runner = null;
      this.balls = [];
      this.winnerBody = null;
    },
  },
};
</script>

<style scoped>
.gacha-machine {
  position: relative;
  width: 380px;
  height: 448px;
  max-width: 92vw;
  margin: 0 auto;
}

.tank-glow {
  position: absolute;
  left: 14px;
  top: 4px;
  width: 352px;
  height: 322px;
  border-radius: 180px 180px 28px 28px;
  background: radial-gradient(circle at 40% 28%, rgba(255, 255, 255, 0.16), rgba(124, 92, 255, 0.1) 55%, rgba(22, 16, 45, 0.25) 100%);
  border: 2px solid rgba(201, 184, 255, 0.4);
  box-shadow: 0 0 40px rgba(124, 92, 255, 0.4), inset 0 0 40px rgba(177, 91, 255, 0.18);
  backdrop-filter: blur(2px);
  pointer-events: none;
}

.canvas-host {
  position: absolute;
  inset: 0;
  z-index: 1;
}

.chute {
  position: absolute;
  left: 50%;
  bottom: 4px;
  transform: translateX(-50%);
  width: 140px;
  height: 68px;
  border-radius: 10px 10px 16px 16px;
  border: 2px solid rgba(201, 184, 255, 0.4);
  border-top: none;
  background: linear-gradient(180deg, rgba(22, 16, 45, 0.1), rgba(22, 16, 45, 0.5));
  pointer-events: none;
}
</style>
